/**
 * Flat workflow picker modal (P4.T5).
 *
 * Opened by WorkflowsCard's "+ Add workflow" button. Mirrors
 * `SkillPickerModal`'s flat-catalog shape — NOT two-step like
 * `ToolBindingPickerModal` (workflows have no per-install choice
 * that warrants step 2; channel defaults to "*" and is customized
 * inline on the installed row).
 *
 * Click a workflow row → composerStore.installWorkflow(workflow_id)
 * → close. The installed binding's `autostart_on_channel` defaults
 * to "*" (wildcard); user narrows via the inline Select on the
 * WorkflowsCard row. When the picked workflow's template manifest
 * carries recommended persona starter blocks (SA-31), closing hands
 * off to StarterBlocksOfferDialog — the binding is installed either
 * way; the dialog only decides whether blocks are appended too.
 *
 * Filters out workflows already installed in `bundle.workflow_bindings`,
 * then narrows the remainder by a client-side name/description search.
 *
 * Catalog-loading skeleton + catalog-error Alert with Retry. No
 * dependencies on the tool catalog (capabilities/adapters) — the
 * picker works even when other catalogs are unreachable.
 *
 * Row content is the shared `WorkflowGalleryCard` (SA-27) — same card
 * body as the `/workflows` gallery (audience badge, "what it needs"
 * chips, self-test tile, kind/source/customized/version badges,
 * language badges), rendered inside this modal's install `<button>`.
 *
 * **NOT lifted to a shared `<CatalogPickerModal>`** — see plan §5
 * decision-log entry "P4.T5: NOT lifting picker modal to shared
 * <CatalogPickerModal> even on this third copy". Only the CARD is
 * shared with the gallery page; the modal shell stays this file's own.
 */
import { useState } from 'react'
import { Search } from 'lucide-react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
} from '@/components/ui/Modal'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Input, Skeleton } from '@/components/ui'
import { WorkflowGalleryCard } from '@/components/workflows/WorkflowGalleryCard'
import { StarterBlocksOfferDialog } from './StarterBlocksOfferDialog'
import { useComposerStore } from '@/stores/composerStore'
import { useWorkflows, type WorkflowSummary } from '@/hooks/useWorkflows'

export interface WorkflowPickerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function WorkflowPickerModal({ open, onOpenChange }: WorkflowPickerModalProps) {
  const draft = useComposerStore((s) => s.draftBundle)
  const server = useComposerStore((s) => s.serverBundle)
  const installWorkflow = useComposerStore((s) => s.installWorkflow)
  const query = useWorkflows()

  const base = draft ?? server
  const installedIds = new Set<string>(
    ((base?.workflow_bindings ?? []) as Array<{ workflow_id: string }>).map(
      (b) => b.workflow_id,
    ),
  )

  const catalog = query.data ?? []
  const available = catalog.filter((w) => !installedIds.has(w.id))

  const [search, setSearch] = useState('')
  // SA-31: workflow whose starter blocks are being offered post-install.
  const [offer, setOffer] = useState<WorkflowSummary | null>(null)
  const q = search.trim().toLowerCase()
  const filtered = q
    ? available.filter(
        (w) =>
          w.name.toLowerCase().includes(q) ||
          (w.description ?? '').toLowerCase().includes(q),
      )
    : available

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setSearch('')
    onOpenChange(nextOpen)
  }

  const handleSelect = (workflow: WorkflowSummary) => {
    installWorkflow(workflow.id)
    handleOpenChange(false)
    if ((workflow.manifest?.persona_blocks ?? []).length > 0) {
      setOffer(workflow)
    }
  }

  return (
    <>
      {offer && (
        <StarterBlocksOfferDialog
          key={offer.id}
          workflowName={offer.name}
          blocks={offer.manifest?.persona_blocks ?? []}
          onClose={() => setOffer(null)}
        />
      )}
      <Modal open={open} onOpenChange={handleOpenChange}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Add a workflow</ModalTitle>
        </ModalHeader>
        <ModalBody>
          {query.isLoading && (
            <div className="space-y-3" data-testid="catalog-loading">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 rounded-sm" />
              ))}
            </div>
          )}
          {query.isError && (
            <Alert variant="destructive" className="text-sm">
              <div className="flex items-center justify-between gap-3">
                <span>Unable to load the workflow catalog.</span>
                <Button variant="ghost" size="sm" onClick={() => query.refetch()}>
                  Retry
                </Button>
              </div>
            </Alert>
          )}
          {query.isSuccess && available.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center">
              All available workflows are already installed. Remove one to add a different
              binding.
            </p>
          )}
          {query.isSuccess && available.length > 0 && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search workflows…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              {filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">
                  No workflows match your search.
                </p>
              ) : (
                <ul className="space-y-2">
                  {filtered.map((workflow) => (
                    <li key={workflow.id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(workflow)}
                        className="w-full text-left p-3 rounded-sm border border-border hover:bg-muted transition"
                      >
                        <WorkflowGalleryCard workflow={workflow} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </ModalBody>
      </ModalContent>
      </Modal>
    </>
  )
}
